import { isCancel } from 'axios';
import { markRaw } from 'vue';
import { type SafeAny } from '@/common/types';
import { EPOCH_SIZES, axiosInstance, capitalizeFirstLetter, i18n } from '@/common/utils';
import { getBaseEpochCount, getStudyData } from '@/modules/study/data-access';
import { StudyConfigParams } from '@/modules/study/types';
import {
  filterSignalMap,
  filterSignalMapChunked,
  type BandFilterConfig
} from './signal-band-filter';
import {
  decodeEpochBinaryChunk,
  isBinarySignalUrl,
  materializeEpochFromChunk,
  type BinaryChunk
} from './signalBinary';

const BATCH_SIZE = 10; // number of requests to make in parallel
const BATCH_DELAY = 600; // delay between batches in milliseconds
const BASE_EPOCH_SIZE = Math.min(...EPOCH_SIZES.map((size) => size.inSeconds));
// Mirrors STORE_EPOCH_CACHE_LIMIT in mutations/index.ts. Kept locally so the
// preloader can size its queue to fit; if you raise one, raise both.
const STORE_EPOCH_CACHE_LIMIT_HINT = 150;
const TARGET_SAMPLES_PER_WINDOW = 1200;
const NO_DOWNSAMPLE_THRESHOLD = 60; // seconds — views up to 60s render at full resolution
const INTEGER_SIGNALS = new Set(['SpO2', 'Heart_rate', 'Position', 'Position_mapped']);
// Signals exempted from min/max downsampling at any window size.
//
// Min/max bin rendering (`buildFastMultiYPath`) draws a sequence of disjoint
// vertical strokes (M,pyMin → L,pyMax → L,nextPyMin → L,nextPyMax). Adjacent
// bins connect at min↔max corners rather than at the actual signal value at
// the column boundary, producing a "picket fence" look that's visually
// distinct from a true polyline through samples — most noticeable on signals
// with sharp high-amplitude transients (QRS complexes on ECG). Bin tightening
// can't close that gap because the artifact is from disjoint strokes, not bin
// width. Whitelisted signals are rendered raw at every window size; the fast
// scalar path (`buildFastPath`) draws a real polyline through every sample,
// which reads correctly as a waveform.
//
// How to whitelist:
//   - Exact name match in NO_DOWNSAMPLE_SIGNALS (use for solo channels like ECG).

const NO_DOWNSAMPLE_SIGNALS = new Set(['ECG']);

const shouldSkipDownsample = (name: string): boolean => NO_DOWNSAMPLE_SIGNALS.has(name);

// Max decoded epochs to keep in the raw cache.
// At ~16 signals × 3840 floats each = ~245 KB per epoch (Float64).
// 200 epochs × 245 KB ≈ 49 MB — safely within the ~500 MB V8 heap limit.
const RAW_EPOCH_CACHE_LIMIT = 200;
// Max binary chunks to keep. Each chunk holds an ArrayBuffer (typically 1–5 MB).
const BINARY_CHUNK_CACHE_LIMIT = 30;

/**
 * Minimal LRU cache backed by a Map whose insertion order gives us FIFO eviction.
 * Map iteration order is guaranteed insertion order in ES2015+.
 */
class LruCache<K, V> {
  private readonly map = new Map<K, V>();
  constructor(private readonly limit: number) {}
  get(key: K): V | undefined {
    return this.map.get(key);
  }
  has(key: K): boolean {
    return this.map.has(key);
  }
  set(key: K, value: V): void {
    if (this.map.has(key))
      this.map.delete(key); // Refresh position
    else if (this.map.size >= this.limit) {
      // Evict the oldest entry (first key in insertion order)
      this.map.delete(this.map.keys().next().value);
    }
    this.map.set(key, value);
  }
  delete(key: K): void {
    this.map.delete(key);
  }
  clear(): void {
    this.map.clear();
  }
}

// Stores { promise, signal } so we can skip reusing a promise whose signal was already aborted.
// This prevents the fast-navigation blank-screen race: if background preload for epoch N was
// started with bgSignal, and the user then navigates to N (aborting bgSignal as a jump), the
// foreground call should NOT reuse the doomed bgSignal promise — it must start a fresh request.
const inFlightEpochRequests = new Map<
  string,
  { promise: Promise<SafeAny | undefined>; signal: AbortSignal }
>();
const rawEpochCache = new LruCache<string, SafeAny>(RAW_EPOCH_CACHE_LIMIT);
const binaryChunkCache = new LruCache<string, BinaryChunk>(BINARY_CHUNK_CACHE_LIMIT);
const inFlightChunkRequests = new Map<string, Promise<BinaryChunk>>();

export const clearSignalCaches = (options?: { clearBinary?: boolean }) => {
  inFlightEpochRequests.clear();
  rawEpochCache.clear();
  if (options?.clearBinary) {
    binaryChunkCache.clear();
    inFlightChunkRequests.clear();
  }
};

const fetchBinaryChunk = async (
  url: string,
  params: Record<string, SafeAny>
): Promise<BinaryChunk> => {
  const existing = inFlightChunkRequests.get(url);
  if (existing) return existing;

  const request = axiosInstance
    .get(url, { params, responseType: 'arraybuffer' })
    .then((response) => {
      if (!response)
        return Promise.reject(Object.assign(new Error('cancelled'), { __CANCEL__: true }));
      return decodeEpochBinaryChunk(response.data as ArrayBuffer);
    })
    .finally(() => {
      inFlightChunkRequests.delete(url);
    });

  inFlightChunkRequests.set(url, request);
  return request;
};

const isBinaryEpochRef = (
  payload: SafeAny
): payload is { __binaryRef: { url: string; epoch: number } } =>
  !!payload && typeof payload === 'object' && !!payload.__binaryRef?.url;

const makeEpochRequestKey = (epoch: string, epochSize: number, state: SafeAny) => {
  const notch = state?.settings?.notch ?? '';
  const studyId = state?.epochData?.selectedStudy ?? '';
  return `${studyId}|${epochSize}|${notch}|${epoch}`;
};

const makeRawEpochKey = (epoch: string, state: SafeAny, rootState: SafeAny) => {
  const notch = state?.settings?.notch ?? '';
  const studyId = rootState?.study?.config?.data?.id || state?.epochData?.selectedStudy || '';
  return `${studyId}|${notch}|${epoch}`;
};

const signalSampleRateCache = new LruCache<string, Record<string, number>>(100);

const resolveSignalSampleRate = (
  rootState: SafeAny,
  state: SafeAny,
  label: string,
  values: number[],
  durationSeconds: number | undefined
): number | undefined => {
  const studyId = rootState?.study?.config?.data?.id || state?.epochData?.selectedStudy;
  if (!studyId) return undefined;

  let rates = signalSampleRateCache.get(studyId);
  if (!rates) {
    rates = {};
    signalSampleRateCache.set(studyId, rates);
  }

  if (rates[label]) return rates[label];

  if (!Array.isArray(values) || values.length === 0) return undefined;
  if (!Number.isFinite(durationSeconds) || (durationSeconds as number) <= 0) return undefined;

  const fs = values.length / (durationSeconds as number);
  rates[label] = fs;
  return fs;
};

const resolveBandFilterConfig = (
  state: SafeAny,
  rootState: SafeAny,
  durationSeconds: number | undefined
): BandFilterConfig => ({
  cutoffFor: (label: string) =>
    state?.settings?.band === false
      ? undefined
      : state?.settings?.signalsList?.find((item: SafeAny) => item.name === label)?.bandFilter,
  sampleRateFor: (label: string, values: number[]) =>
    resolveSignalSampleRate(rootState, state, label, values, durationSeconds)
});

const applyBandFilterToPayload = async (
  payload: SafeAny,
  state: SafeAny,
  rootState: SafeAny,
  durationSeconds: number | undefined,
  abortSignal?: AbortSignal
): Promise<SafeAny> => {
  if (!payload?.signal) return payload;
  const config = resolveBandFilterConfig(state, rootState, durationSeconds);
  const filteredSignal = await filterSignalMapChunked(
    payload.signal,
    config,
    payload,
    '',
    abortSignal
  );
  return filteredSignal === payload.signal ? payload : { ...payload, signal: filteredSignal };
};

const applyBandFilterToChunkEpoch = async (
  payload: SafeAny,
  chunkOwner: object,
  epochIndex: number,
  state: SafeAny,
  rootState: SafeAny,
  durationSeconds: number | undefined,
  abortSignal?: AbortSignal
): Promise<SafeAny> => {
  if (!payload?.signal) return payload;

  const config = resolveBandFilterConfig(state, rootState, durationSeconds);
  const filteredSignal = await filterSignalMapChunked(
    payload.signal,
    config,
    chunkOwner,
    `${epochIndex}|`,
    abortSignal
  );

  return filteredSignal === payload.signal ? payload : { ...payload, signal: filteredSignal };
};

const applyBandFilterToChunkEpochSync = (
  payload: SafeAny,
  chunkOwner: object,
  epochIndex: number,
  state: SafeAny,
  rootState: SafeAny,
  durationSeconds: number | undefined
): SafeAny => {
  if (!payload?.signal) return payload;

  const config = resolveBandFilterConfig(state, rootState, durationSeconds);
  const filteredSignal = filterSignalMap(payload.signal, config, chunkOwner, `${epochIndex}|`);

  return filteredSignal === payload.signal ? payload : { ...payload, signal: filteredSignal };
};

const resolveFullNightDurationSeconds = (
  rootState: SafeAny,
  state: SafeAny
): number | undefined => {
  const id = rootState?.study?.config?.data?.id || state?.epochData?.selectedStudy;
  const notch = state?.settings?.notch;
  if (!id || !notch) return undefined;
  const baseEpochCount = getBaseEpochCount(rootState, id, notch, 'False');
  return baseEpochCount ? baseEpochCount * BASE_EPOCH_SIZE : undefined;
};

const resolveEpochDurationSeconds = (
  epochSize: number,
  rootState: SafeAny,
  state: SafeAny
): number | undefined =>
  epochSize === -1 ? resolveFullNightDurationSeconds(rootState, state) : BASE_EPOCH_SIZE;

const getAdjacentEpochSizes = (currentEpochSize: number): number[] => {
  const sortedEpochSizes = EPOCH_SIZES.map((size) => size.inSeconds).sort((a, b) => a - b);
  return sortedEpochSizes
    .map((size) => ({ size, diff: Math.abs(size - currentEpochSize) }))
    .sort((a, b) => a.diff - b.diff)
    .map(({ size }) => size);
};

const buildBaseEpochRange = (epochIndex: number, epochSize: number, baseEpochCount: number) => {
  if (baseEpochCount <= 0) return null;
  const ratio = epochSize / BASE_EPOCH_SIZE;
  if (!Number.isInteger(ratio) || ratio <= 0) return null;
  const start = epochIndex * ratio;
  if (start >= baseEpochCount) return null;
  const end = Math.min(start + ratio - 1, baseEpochCount - 1);
  return { start, end };
};

const downsampleSignal = (signalName: string, values: SafeAny[], targetSamples: number) => {
  if (!Array.isArray(values) || values.length <= targetSamples || targetSamples <= 0) {
    return values;
  }

  const bucketSize = values.length / targetSamples;
  const isIntegerSignal = INTEGER_SIGNALS.has(signalName);
  const downsampled: SafeAny[] = [];

  for (let bucket = 0; bucket < targetSamples; bucket += 1) {
    const start = Math.floor(bucket * bucketSize);
    let end = Math.floor((bucket + 1) * bucketSize);
    if (end <= start) end = start + 1;
    if (start >= values.length) break;
    if (end > values.length) end = values.length;

    if (isIntegerSignal) {
      let lastValue: number | null = null;
      for (let i = start; i < end; i += 1) {
        const sample = values[i];
        const raw = Array.isArray(sample)
          ? sample.length > 1
            ? (sample[0] + sample[1]) / 2
            : sample[0]
          : sample;
        if (Number.isFinite(raw)) {
          lastValue = raw;
        }
      }
      downsampled.push([lastValue !== null ? Math.round(lastValue) : NaN]);
      continue;
    }

    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;
    let found = false;
    for (let i = start; i < end; i += 1) {
      const sample = values[i];
      if (Array.isArray(sample)) {
        const minVal = sample[0];
        const maxVal = sample.length > 1 ? sample[1] : sample[0];
        if (Number.isFinite(minVal)) {
          min = Math.min(min, minVal);
          max = Math.max(max, minVal);
          found = true;
        }
        if (Number.isFinite(maxVal)) {
          min = Math.min(min, maxVal);
          max = Math.max(max, maxVal);
          found = true;
        }
      } else if (Number.isFinite(sample)) {
        min = Math.min(min, sample);
        max = Math.max(max, sample);
        found = true;
      }
    }
    downsampled.push(found ? [min, max] : [NaN, NaN]);
  }

  return downsampled;
};

// @visibleForTesting — exported only for unit tests
export { downsampleSignal as _downsampleSignal };
export { mergeEpochPayloads as _mergeEpochPayloads };
export const _NO_DOWNSAMPLE_THRESHOLD = NO_DOWNSAMPLE_THRESHOLD;
export const _TARGET_SAMPLES_PER_WINDOW = TARGET_SAMPLES_PER_WINDOW;
export const _BASE_EPOCH_SIZE = BASE_EPOCH_SIZE;

const downsampleSignalsForWindow = (signals: Record<string, SafeAny[]>, targetSamples: number) => {
  const reduced: Record<string, SafeAny[]> = {};
  for (const [label, values] of Object.entries(signals)) {
    if (shouldSkipDownsample(label)) {
      // Pass-through raw — see NO_DOWNSAMPLE_SIGNALS comment above for the
      // rendering-artifact rationale. `downsampleSignal` early-returns for
      // signals already at or below the target, so calling it would have
      // worked too at sub-target counts; this explicit branch makes the
      // intent clear and avoids any future change to the early-return guard
      // accidentally re-enabling downsampling on whitelisted signals.
      reduced[label] = values;
      continue;
    }
    reduced[label] = downsampleSignal(label, values, targetSamples);
  }
  return reduced;
};

/**
 * Wraps integer-signal scalar samples as [value] single-element arrays, matching the
 * shape produced by downsampleSignal's integer branch.  At ≥5 min windows signals go
 * through downsampling, which wraps integer signals naturally.  At <2 min windows
 * (30 s, 60 s) we skip downsampling for resolution — this function ensures
 * those views see the same multi-Y shape so downstream rendering (artifact handling
 * in convertMultipleYValuesIntoLine, label text coercion, etc.) behaves identically.
 */
const wrapIntegerSignalsInPlace = (signals: Record<string, SafeAny[]>) => {
  for (const label of Object.keys(signals)) {
    if (!INTEGER_SIGNALS.has(label)) continue;
    const values = signals[label];
    if (!Array.isArray(values) || values.length === 0) continue;
    if (Array.isArray(values[0])) continue; // Already wrapped — don't double-wrap
    const wrapped = new Array(values.length);
    for (let i = 0; i < values.length; i += 1) {
      wrapped[i] = [values[i]];
    }
    signals[label] = wrapped;
  }
};

const mergeRawEpochPayloads = (payloads: SafeAny[]): SafeAny | undefined => {
  const validPayloads = payloads.filter(Boolean);
  if (validPayloads.length === 0) return undefined;

  const combinedSignal: Record<string, number[][]> = {};
  const combinedMapping: Record<string, SafeAny> = {};

  for (const payload of validPayloads) {
    const signalEntries = payload.signal || {};
    for (const [label, values] of Object.entries(signalEntries)) {
      const arr = values as number[][];
      if (!combinedSignal[label]) {
        // First payload: take the array directly instead of copying element-by-element.
        // This avoids the O(n) spread that was a major bottleneck for large signals.
        combinedSignal[label] = arr;
      } else {
        // Subsequent payloads: use concat (engine-optimized) instead of push(...arr)
        // which spreads 7680+ elements as function arguments, causing stack overflow
        // risk and massive GC pressure from intermediate argument lists.
        combinedSignal[label] = combinedSignal[label].concat(arr);
      }
    }

    const mappingEntries = payload.mapping || {};
    for (const [label, mapping] of Object.entries(mappingEntries)) {
      if (!combinedMapping[label]) {
        combinedMapping[label] = { ...(mapping as SafeAny) };
        continue;
      }
      const target = combinedMapping[label] as SafeAny;
      const source = mapping as SafeAny;
      if (Number.isFinite(source.min)) {
        target.min = Number.isFinite(target.min) ? Math.min(target.min, source.min) : source.min;
      }
      if (Number.isFinite(source.max)) {
        target.max = Number.isFinite(target.max) ? Math.max(target.max, source.max) : source.max;
      }
    }
  }

  return {
    signal: combinedSignal,
    mapping: combinedMapping,
    baseEpochCount: validPayloads.length,
    ...(validPayloads[0]?.glue_version ? { glue_version: validPayloads[0].glue_version } : {})
  };
};

const finalizeMergedEpochPayload = (merged: SafeAny, epochSize?: number): SafeAny => {
  let mergedSignal: Record<string, SafeAny[]>;
  if (typeof epochSize === 'number' && epochSize > NO_DOWNSAMPLE_THRESHOLD) {
    mergedSignal = downsampleSignalsForWindow(merged.signal, TARGET_SAMPLES_PER_WINDOW);
  } else {
    wrapIntegerSignalsInPlace(merged.signal);
    mergedSignal = merged.signal;
  }

  const result = markRaw({
    signal: mergedSignal,
    mapping: merged.mapping
  } as SafeAny);

  if (merged.glue_version) {
    result.glue_version = merged.glue_version;
  }

  return result;
};

const mergeEpochPayloads = (payloads: SafeAny[], epochSize?: number) => {
  const raw = mergeRawEpochPayloads(payloads);
  return raw ? finalizeMergedEpochPayload(raw, epochSize) : undefined;
};

const getRawBaseEpochData = async (
  epoch: string,
  state: SafeAny,
  rootState: SafeAny,
  signal: AbortSignal
): Promise<SafeAny | undefined> => {
  const cacheKey = makeRawEpochKey(epoch, state, rootState);
  const cached = rawEpochCache.get(cacheKey);
  if (cached) return cached;

  const epochData = rootState.study.config.data?.sets?.[BASE_EPOCH_SIZE]?.[epoch];
  if (!epochData) return undefined;

  const { url, ...urlCredentials } = epochData;
  const credentials = rootState.study.config.data?.config;
  const isBinary = isBinarySignalUrl(url);
  const epochIndex = Number.isFinite(Number(epoch)) ? Number(epoch) : 0;

  let resolvedData;
  if (isBinary) {
    const params = { ...credentials, ...urlCredentials };
    const chunk = binaryChunkCache.get(url) || (await fetchBinaryChunk(url, params));
    if (!binaryChunkCache.has(url)) {
      binaryChunkCache.set(url, chunk);
      prefetchAdjacentBinaryChunk(rootState, chunk.chunk, credentials, 'next');
      prefetchAdjacentBinaryChunk(rootState, chunk.chunk, credentials, 'previous');
    }
    resolvedData = materializeEpochFromChunk(chunk, epochIndex);
    if (!resolvedData) {
      const firstKey = Object.keys(chunk.epochs || {})[0];
      if (firstKey !== undefined) {
        resolvedData = materializeEpochFromChunk(chunk, Number(firstKey));
      }
    }
  } else {
    const response = await axiosInstance.get(url, {
      params: {
        ...credentials,
        ...urlCredentials
      },
      signal,
      responseType: 'json'
    });
    resolvedData = response.data?.signal ? response.data : { signal: response.data };
  }

  if (resolvedData) {
    // Wrap integer signals once at cache-population time so all downstream consumers
    // (mergeEpochPayloads for derived sizes, direct 30s render) see the same shape.
    if (resolvedData.signal) {
      wrapIntegerSignalsInPlace(resolvedData.signal);
    }
    // Mark raw to prevent Vue from deep-observing signal arrays when cached data
    // flows into the reactive store via mergeEpochPayloads or direct assignment
    if (typeof resolvedData === 'object') markRaw(resolvedData);
    if (!isBinary) {
      rawEpochCache.set(cacheKey, resolvedData);
    }
  }

  return resolvedData;
};

const prefetchAdjacentBinaryChunk = (
  rootState: SafeAny,
  currentChunk: { start_epoch: number; count: number } | undefined,
  credentials: Record<string, SafeAny>,
  direction: 'next' | 'previous'
) => {
  if (!currentChunk) return;
  const targetEpoch =
    direction === 'next'
      ? currentChunk.start_epoch + currentChunk.count
      : currentChunk.start_epoch - 1;
  if (targetEpoch < 0) return;
  const entry = rootState.study.config.data?.sets?.[BASE_EPOCH_SIZE]?.[targetEpoch];
  if (!entry?.url) return;
  if (binaryChunkCache.has(entry.url) || inFlightChunkRequests.has(entry.url)) return;

  const { url, ...urlCredentials } = entry;
  void fetchBinaryChunk(url, { ...credentials, ...urlCredentials })
    .then((decoded) => {
      binaryChunkCache.set(url, decoded);
    })
    .catch(() => undefined);
};

export const loadEpochData = async (
  epoch: string,
  epochSize: number,
  state: SafeAny,
  rootState: SafeAny,
  signal: AbortSignal,
  commit: SafeAny
): Promise<SafeAny | undefined> => {
  if (!state?.epochData?.sets) {
    state.epochData.sets = {};
  }

  const durationSeconds = resolveEpochDurationSeconds(epochSize, rootState, state);

  const cached = state?.epochData?.sets?.[epochSize]?.[epoch];
  if (cached) {
    if (isBinaryEpochRef(cached)) {
      const cachedChunk = binaryChunkCache.get(cached.__binaryRef.url);
      if (cachedChunk) {
        const materialized = materializeEpochFromChunk(cachedChunk, cached.__binaryRef.epoch);
        if (materialized) {
          // materializeEpochFromChunk always produces fresh flat arrays from the raw buffer.
          // Wrap ALL integer signals (Position, SpO2, Heart_rate, Position_mapped) so this
          // cache-hit path is shape-consistent with the first-fetch binary path
          // (loadEpochData line ~552 calls wrapIntegerSignalsInPlace on resolvedData).
          //
          // Earlier this only wrapped Position — the rationale being that SpO2/Heart_rate
          // were "high-frequency and benefit from the fast rendering path" (which expects
          // unwrapped scalars). But that left the SAME signal in two different shapes
          // depending on whether the chunk was hit fresh or via cache: the current epoch
          // came back unwrapped from this cache path, while the adjacent epoch — fetched
          // via getSignalFromEpochEntry which does call wrapIntegerSignalsInPlace —
          // came back wrapped. During granular wheel navigation paintChart concats
          // current+adjacent into a single fastYData array; the d3 layer's
          // useMultiYFastPath check (`Array.isArray(rawYData[0])`) then activated based
          // on which half the slice started in, and `buildFastMultiYPath` skipped every
          // single-element wrapped bucket via its `pair.length < 2` guard, leaving an
          // empty path string and the SpO2 curve invisible while labels and events
          // (which iterate this.data with shape-tolerant code) kept rendering. Tooltip
          // code in Spo2SignalGraphD3.buildTooltipContent also threw on unwrapped p.y
          // because `(p.y as number[]).filter(...)` is invalid on a scalar.
          //
          // Wrapping here is O(samples) once per cache hit — the cost is paid by
          // adjacent-epoch reads during navigation, not the (already slow) initial
          // fetch. The fast scalar render path no longer activates for SpO2/Heart_rate,
          // but the multi-Y slow path is correct and the difference is imperceptible
          // for these rates (SpO2 ~64 Hz, Heart_rate ~64 Hz).
          wrapIntegerSignalsInPlace(materialized.signal);
          try {
            return await applyBandFilterToChunkEpoch(
              materialized,
              cachedChunk,
              cached.__binaryRef.epoch,
              state,
              rootState,
              durationSeconds,
              signal
            );
          } catch (error) {
            if (isCancel(error) || signal?.aborted) return undefined;
            throw error;
          }
        }
      }
    } else {
      return cached;
    }
  }

  const key = makeEpochRequestKey(epoch, epochSize, state);

  const existing = inFlightEpochRequests.get(key);
  if (existing) {
    // Only reuse the in-flight promise if its original signal has NOT been aborted.
    // If the promise was started by a background preload whose signal was later aborted
    // (e.g., via backgroundAbortController.abort() on a jump navigation), returning it
    // would give the foreground caller a promise that resolves to undefined — causing
    // a blank display that only clears on the next navigation cycle.
    if (!existing.signal.aborted) return existing.promise;
    inFlightEpochRequests.delete(key); // Discard the stale aborted entry
  }

  const requestPromise = (async () => {
    const runFetch = async () => {
      const isDerivedEpochSize = epochSize !== BASE_EPOCH_SIZE && epochSize !== -1;

      if (isDerivedEpochSize) {
        const baseEpochCount = rootState.study.config.data?.sets?.[BASE_EPOCH_SIZE]?.length || 0;
        const epochIndex = Number.isFinite(Number(epoch)) ? Number(epoch) : 0;
        const epochConfig = rootState.study.config.data?.sets?.[epochSize]?.[epochIndex];
        const range =
          epochConfig?.baseEpochStart !== undefined
            ? {
                start: epochConfig.baseEpochStart,
                end: epochConfig.baseEpochEnd ?? epochConfig.baseEpochStart
              }
            : buildBaseEpochRange(epochIndex, epochSize, baseEpochCount);
        if (!range) return undefined;

        const baseEpochs: string[] = [];
        for (let idx = range.start; idx <= range.end; idx += 1) {
          baseEpochs.push(String(idx));
        }

        const basePayloads = await Promise.all(
          baseEpochs.map((baseEpoch) => getRawBaseEpochData(baseEpoch, state, rootState, signal))
        );
        const rawMerged = mergeRawEpochPayloads(basePayloads as SafeAny[]);
        if (!rawMerged) return undefined;

        const derivedDurationSeconds = rawMerged.baseEpochCount * BASE_EPOCH_SIZE;
        const filteredMerged = await applyBandFilterToPayload(
          rawMerged,
          state,
          rootState,
          derivedDurationSeconds,
          signal
        );
        const merged = finalizeMergedEpochPayload(filteredMerged, epochSize);

        commit('setEpochDataSet', { epochSize, epoch, responseData: merged });
        return merged;
      }

      const epochData = rootState.study.config.data?.sets?.[epochSize]?.[epoch];
      if (!epochData) return undefined;

      const { url, ...urlCredentials } = epochData;
      const credentials = rootState.study.config.data?.config;

      const isBinary = isBinarySignalUrl(url);
      const epochIndex = Number.isFinite(Number(epoch)) ? Number(epoch) : 0;

      if (isBinary && binaryChunkCache.has(url)) {
        const cachedChunk = binaryChunkCache.get(url);
        const cachedData = cachedChunk
          ? materializeEpochFromChunk(cachedChunk, epochIndex)
          : undefined;
        if (cachedData && cachedChunk) {
          // materializeEpochFromChunk always produces fresh flat arrays — wrap ALL
          // integer signals (Position, SpO2, Heart_rate, Position_mapped) so this
          // cache-hit path is shape-consistent with the first-fetch path below
          // (which calls `wrapIntegerSignalsInPlace(resolvedData.signal)`). See the
          // longer comment at the parallel call site above (in the cached-entry
          // branch) for the SpO2 mixed-shape bugs that arise without this wrap:
          // the desat hover tooltip throws on `(scalar).filter`, and during wheel
          // navigation across epoch boundaries the SpO2 curve disappears because
          // `buildFastMultiYPath` skips every wrapped single-element bucket.
          wrapIntegerSignalsInPlace(cachedData.signal);
          const binaryRef = { __binaryRef: { url, epoch: epochIndex } };
          commit('setEpochDataSet', { epochSize, epoch, responseData: binaryRef });
          return applyBandFilterToChunkEpoch(
            cachedData,
            cachedChunk,
            epochIndex,
            state,
            rootState,
            durationSeconds,
            signal
          );
        }
      }

      if (!isBinary && epochSize === BASE_EPOCH_SIZE) {
        const rawCached = rawEpochCache.get(makeRawEpochKey(epoch, state, rootState));
        if (rawCached) {
          const filteredFromRawCache = await applyBandFilterToPayload(
            rawCached,
            state,
            rootState,
            durationSeconds,
            signal
          );
          commit('setEpochDataSet', { epochSize, epoch, responseData: filteredFromRawCache });
          return filteredFromRawCache;
        }
      }

      let resolvedData;
      let sourceChunk: BinaryChunk | undefined;
      if (isBinary) {
        const params = { ...credentials, ...urlCredentials };
        const chunk = await fetchBinaryChunk(url, params);
        binaryChunkCache.set(url, chunk);
        prefetchAdjacentBinaryChunk(rootState, chunk.chunk, credentials, 'next');
        prefetchAdjacentBinaryChunk(rootState, chunk.chunk, credentials, 'previous');
        sourceChunk = chunk;
        resolvedData = materializeEpochFromChunk(chunk, epochIndex);

        if (!resolvedData) {
          const firstKey = Object.keys(chunk.epochs || {})[0];
          if (firstKey !== undefined) {
            resolvedData = materializeEpochFromChunk(chunk, Number(firstKey));
          }
        }
      } else {
        const response = await axiosInstance.get(url, {
          params: {
            ...credentials,
            ...urlCredentials
          },
          signal,
          responseType: 'json'
        });
        resolvedData = response.data?.signal ? response.data : { signal: response.data };
      }

      if (!resolvedData) return undefined;

      // Wrap integer signals as [value] single-element arrays — matches the shape
      // produced by downsampleSignal at ≥5 min so all windows route scalar integer
      // signals through the same multi-Y rendering path (convertMultipleYValuesIntoLine).
      if (resolvedData.signal) {
        wrapIntegerSignalsInPlace(resolvedData.signal);
      }

      if (epochSize === BASE_EPOCH_SIZE) {
        const cacheKey = makeRawEpochKey(epoch, state, rootState);
        rawEpochCache.set(cacheKey, resolvedData);
      }

      // If the glue job does the conversion, it's detected by the updated unit to 'mV'
      const emgUnit =
        resolvedData?.mapping?.EMG_leg?.unit && String(resolvedData.mapping.EMG_leg.unit);
      if (
        resolvedData?.signal &&
        resolvedData.mapping &&
        Array.isArray(resolvedData.signal.EMG_leg) &&
        emgUnit &&
        emgUnit.toLowerCase() === 'mv'
      ) {
        Object.assign(resolvedData.mapping.EMG_leg, {
          min: resolvedData.mapping.EMG_leg.min * 1000,
          max: resolvedData.mapping.EMG_leg.max * 1000
        });

        resolvedData.signal.EMG_leg = resolvedData.signal.EMG_leg.map((item: any) => {
          if (Array.isArray(item)) {
            return item.map((val: number) => val * 1000);
          }

          return item * 1000;
        });
      }

      // Mark signal data as raw BEFORE it enters the reactive store — prevents Vue
      // from wrapping ~130K+ nested arrays with reactive proxies (was ~230ms overhead).
      if (resolvedData && typeof resolvedData === 'object') markRaw(resolvedData);

      if (isBinary) {
        const binaryRef = { __binaryRef: { url, epoch: epochIndex } };
        commit('setEpochDataSet', { epochSize, epoch, responseData: binaryRef });
        return applyBandFilterToChunkEpoch(
          resolvedData,
          sourceChunk as BinaryChunk,
          epochIndex,
          state,
          rootState,
          durationSeconds,
          signal
        );
      }

      const filteredForStore = await applyBandFilterToPayload(
        resolvedData,
        state,
        rootState,
        durationSeconds,
        signal
      );
      commit('setEpochDataSet', { epochSize, epoch, responseData: filteredForStore });

      return filteredForStore;
    };

    try {
      return await runFetch();
    } catch (error: any) {
      if (isCancel(error) || signal?.aborted) {
        return undefined;
      }

      await callStudyData(state, rootState, commit, epochSize);

      throw error;
    } finally {
      inFlightEpochRequests.delete(key);
    }
  })();

  inFlightEpochRequests.set(key, { promise: requestPromise, signal });
  return requestPromise;
};

const callStudyData = async (
  state: SafeAny,
  rootState: SafeAny,
  commit: (type: string, payload: SafeAny, options?: SafeAny) => void,
  epochSize: number
): Promise<void> => {
  const { notch } = state.settings;
  const normalizedEpochSize = epochSize === -1 ? -1 : BASE_EPOCH_SIZE;

  const studyConfigPayload: StudyConfigParams = {
    epochSize: normalizedEpochSize,
    notch: capitalizeFirstLetter(notch),
    band: 'False',
    ...(rootState?.study?.config?.data?.id ? { id: rootState.study.config.data.id } : {}),
    ...({ refreshOnly: true } as any)
  } as any;

  await getStudyData({ rootState, commit }, studyConfigPayload);
};

const loadEpochsInBatchesWithDelay = async (
  epochs: string[],
  epochSize: number,
  state: SafeAny,
  rootState: SafeAny,
  signal: AbortSignal,
  isBackgroundLoadingRef: { value: boolean },
  commit: SafeAny
): Promise<void> => {
  isBackgroundLoadingRef.value = true;
  for (let i = 0; i < epochs.length; i += BATCH_SIZE) {
    const batch = epochs.slice(i, i + BATCH_SIZE);
    await Promise.all(
      batch.map((epoch) => loadEpochData(epoch, epochSize, state, rootState, signal, commit))
    );
    await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY)); // add delay between batches
  }

  isBackgroundLoadingRef.value = false;
};

export const preloadAdjacentEpochs = (
  selectedEpoch: string,
  epochSize: number,
  store: SafeAny,
  signal: AbortSignal,
  isBackgroundLoadingRef: { value: boolean }
): Promise<void> =>
  new Promise((resolve) => {
    (async () => {
      const { t } = i18n.global;
      const { state, rootState, commit } = store;

      if (signal?.aborted) {
        return;
      }

      // Preload the spiral around the user's current epoch using NUMERIC offsets
      // (N+1, N-1, N+2, N-2, …), not array-index arithmetic.
      //
      // Why: rootState.study.config.data.sets[epochSize] is *sparse* for derived
      // sizes (it's populated lazily as the user navigates). Using
      // Object.keys(...).indexOf(selectedEpoch) interprets the user's epoch as a
      // position in that sparse keyset and walks the surrounding array slots —
      // which produces a queue completely unrelated to the actual epoch number.
      // Concretely: if sets[120] has keys ["1",…,"153","273"] and the user is at
      // "273" (array index 153), the old code preloaded "153","152",…,"1" instead
      // of "274","272","275","271",…  Granular forward at 273 then found 274
      // uncached → blank signal.
      //
      // The valid epoch range for a derived size is determined by the base epoch
      // count: ceil(baseEpochCount / ratio) windows. For the base size we fall
      // back to the config's own length. runFetch already supports loading
      // derived epochs whose config entry doesn't exist (via buildBaseEpochRange),
      // so we don't need a config entry to preload.
      const baseEpochCount = rootState.study.config.data?.sets?.[BASE_EPOCH_SIZE]?.length || 0;
      const isDerived = epochSize !== BASE_EPOCH_SIZE && epochSize !== -1;
      const ratio = isDerived ? epochSize / BASE_EPOCH_SIZE : 1;
      const baseSizeLength =
        rootState.study.config.data?.sets?.[epochSize]?.length ||
        Object.keys(rootState.study.config.data?.sets?.[epochSize] || {}).length;
      const totalEpochs = isDerived
        ? ratio > 0 && Number.isInteger(ratio)
          ? Math.floor(baseEpochCount / ratio)
          : 0
        : baseSizeLength;

      const baseN = parseInt(selectedEpoch, 10);
      const adjacentEpochs: string[] = [];
      const cachedSet = state.epochData.sets?.[epochSize] || {};

      if (Number.isFinite(baseN) && totalEpochs > 0) {
        // Cap queue to the cache budget so the preloader doesn't load more entries
        // than the cache can hold (which causes thrash: every late-batch insert
        // evicts a recently-loaded sibling). Reserve a little headroom for the
        // protected ±2 window around displayedEpoch.
        const PROTECTED_RESERVE = 5;
        const cacheBudget = Math.max(STORE_EPOCH_CACHE_LIMIT_HINT - PROTECTED_RESERVE, 1);
        const maxRadius = Math.min(totalEpochs, Math.ceil(cacheBudget / 2) + 1);

        for (let i = 1; i <= maxRadius && adjacentEpochs.length < cacheBudget; i++) {
          const next = baseN + i;
          const prev = baseN - i;
          // Queue NEXT before PREV at each spiral step — sequential forward review
          // is the dominant navigation pattern, and granular-forward needs N+1.
          if (next < totalEpochs && !cachedSet[next]) {
            adjacentEpochs.push(String(next));
          }
          if (adjacentEpochs.length < cacheBudget && prev >= 0 && !cachedSet[prev]) {
            adjacentEpochs.push(String(prev));
          }
        }
      }

      // Preload adjacent epochs
      if (adjacentEpochs.length > 0) {
        await loadEpochsInBatchesWithDelay(
          adjacentEpochs,
          epochSize,
          state,
          rootState,
          signal,
          isBackgroundLoadingRef,
          commit
        );
      }
      resolve();
    })();
  });

export const getSignalFromEpochEntry = (
  entry: SafeAny,
  state: SafeAny,
  rootState: SafeAny,
  epochSize: number
): SafeAny | null => {
  if (!entry) return null;

  if (entry.signal) return entry.signal;

  if (!isBinaryEpochRef(entry)) return null;

  const { url, epoch } = entry.__binaryRef;
  const chunk = binaryChunkCache.get(url);

  if (!chunk) return null;

  const materialized = materializeEpochFromChunk(chunk, epoch);

  if (!materialized) return null;

  // Match the wrapping applied by the first-fetch path (wrapIntegerSignalsInPlace
  // in loadEpochData). materializeEpochFromChunk returns fresh flat arrays from
  // the raw buffer — without wrapping, integer signals (Heart_rate, SpO2, Position)
  // are scalar here while the current epoch's same signals are multi-Y [[v]].
  // When this adjacent data is merged with the current data and routed through
  // d3's convertMultipleYValuesIntoLine, the scalar items are silently dropped
  // (the inner `for (i < rawY.length)` loop treats `undefined` as 0), leaving
  // the labeled signals blank in the adjacent half during granular navigation.
  if (materialized.signal) {
    wrapIntegerSignalsInPlace(materialized.signal);
  }

  const durationSeconds = resolveEpochDurationSeconds(epochSize, rootState, state);
  const filtered = applyBandFilterToChunkEpochSync(
    materialized,
    chunk,
    epoch,
    state,
    rootState,
    durationSeconds
  );

  return filtered?.signal ?? null;
};

export const preloadAdjacentEpochSizeEpochs = (
  currentEpochSize: number,
  store: SafeAny,
  signal: AbortSignal,
  isBackgroundLoadingRef: { value: boolean }
): Promise<void> =>
  new Promise((resolve) => {
    (async () => {
      const { rootState, state, commit } = store;

      if (signal?.aborted) {
        return;
      }

      const allEpochSizes = getAdjacentEpochSizes(currentEpochSize);

      const MAX_ITEMS_SET = 10;
      const epochSizeQueue = [...allEpochSizes];

      while (epochSizeQueue.length > 0) {
        const epochSize: SafeAny = epochSizeQueue.shift();

        if (rootState.study.config.data.sets?.[epochSize]) {
          const loadedEpochs = Object.keys(state.epochData.sets[epochSize] || {});
          const remainingEpochs = MAX_ITEMS_SET - loadedEpochs.length;

          if (remainingEpochs > 0) {
            const epochsToPreload = Object.keys(rootState.study.config.data.sets[epochSize])
              .filter((epoch) => !state.epochData.sets[epochSize]?.[epoch])
              .slice(0, remainingEpochs);

            if (epochsToPreload.length > 0) {
              await loadEpochsInBatchesWithDelay(
                epochsToPreload,
                epochSize,
                state,
                rootState,
                signal,
                isBackgroundLoadingRef,
                commit
              );

              const stillHasCapacity =
                Object.keys(state.epochData.sets[epochSize] || {}).length < MAX_ITEMS_SET;
              const stillHasUnloadedEpochs = Object.keys(
                rootState.study.config.data.sets[epochSize]
              ).some((epoch) => !state.epochData.sets[epochSize]?.[epoch]);

              if (stillHasCapacity && stillHasUnloadedEpochs) {
                epochSizeQueue.push(epochSize);
              }
            }
          }
        }
      }

      resolve();
    })();
  });
